window.addEventListener('load', () => {
  class PickupDynamic extends HTMLElement {
    constructor() {
      super();
      this.unique_id = this.getAttribute('data-unique-id');
      this.containerEl = this.querySelector(`#PICKUP-FORM-${this.unique_id}`);
      this.modal = this.closest('x-modal.modal--pickup');
      this.storeListEl = this.modal?.querySelector(`#PICKUP-STORES-${this.unique_id}`);
      this.form = null;

      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.updateStoreList = this.updateStoreList.bind(this);
    }

    async updateStoreList(variantId) {
      if (!variantId || !this.storeListEl) return;

      try {
        const response = await fetch(`${Shopify.routes.root}variants/${variantId}?section_id=pickup-availability`);
        if (!response.ok) throw new Error(`Failed to fetch variant data: ${response.status}`);
        
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, 'text/html');
        const newContent = html.querySelector(`#PICKUP-STORES-${this.unique_id}`);
        
        if (newContent) {
          this.storeListEl.innerHTML = newContent.innerHTML;
        }
      } catch (error) {
        console.error('Error updating store list:', error);
      }
    }

    handleVariantChange(event) {
      const variant = event.detail?.variant;
      if (!variant) return;

      const outsideVariantPicker = document.querySelector(
        `.shopify-section--main-product variant-picker[form="${this.form?.split('_pickup')[0]}"]`
      );
      
      if (outsideVariantPicker) {
        outsideVariantPicker.selectVariant(variant.id);
      }

      this.updateStoreList(variant.id);
    }
  
    connectedCallback() {
      if (!this.modal?.hasAttribute('open') || !this.containerEl) return;
    
      const childNodeSelectors = [
        'vendor', 'title', 'text', 'price', 
        'product-variations', 'variant-picker', 
        'buy-buttons'
      ];
    
      const parentBlockList = document.querySelector(
        'main section.shopify-section--main-product .product-info__block-list'
      );
    
      if (!parentBlockList) return;
    
      const clonedBlockList = parentBlockList.cloneNode(true);
      this.containerEl.innerHTML = '';
    
      const filteredNodes = Array.from(clonedBlockList.children).filter(node => 
        childNodeSelectors.includes(node.getAttribute('data-block-type'))
      );

      // Create a fragment for better performance
      const fragment = document.createDocumentFragment();
    
      // Create the image container
      const imageContainer = document.createElement('div');
      const imageInnerContainer = document.createElement('div');
      
      // Find the original image and clone it instead of moving it
      const originalImage = this.querySelector('#PICKUP-IMAGE');
      if (originalImage) {
        const clonedImage = originalImage.cloneNode(true);
        clonedImage.classList.remove('hidden');
        imageContainer.appendChild(clonedImage);
      }
      
      imageContainer.classList.add('pickup-availability__image-container');
      imageContainer.appendChild(imageInnerContainer);

      // Move first 4 nodes that match the allowed types into the image container
      const allowedImageTypes = ['vendor', 'title', 'price', 'text'];
      const imageNodes = filteredNodes.filter(node =>
        allowedImageTypes.includes(node.getAttribute('data-block-type'))
      ).slice(0, 4);

      imageNodes.forEach(node => imageInnerContainer.appendChild(node));

      // Append the image container to the fragment
      fragment.appendChild(imageContainer);

      // Filter out the nodes that were moved to the image container
      const remainingNodes = filteredNodes.filter(node => 
        !imageNodes.includes(node)
      );

      // Append the rest of the nodes to the fragment
      remainingNodes.forEach(node => fragment.appendChild(node));
    
      // Process IDs and references
      this.processElementIds(fragment);
    
      // Setup "Add to Bag" button behavior
      this.setupAddToBagButton(fragment);
    
      const containerEl = fragment.querySelector('.product-info__block-item[data-block-type="product-variations"]');
      const anchorElements = containerEl?.querySelectorAll('a');
      anchorElements?.forEach(anchor => {
        const href = anchor.getAttribute('href') || '';
        if (!href.includes('pickup_modal=true')) {
          anchor.setAttribute('href', `${href}${href.includes('?') ? '&' : '?'}pickup_modal=true`);
        }
      });
    
      // Append the processed content to containerEl
      this.containerEl.removeAttribute('style');
      this.containerEl.appendChild(fragment);
    
      // Initialize store list
      this.initializeStoreList();
    }

    processElementIds(fragment) {
      // Map to store original to new ID mappings for reference updates
      const idMap = new Map();
      
      // Find all elements with an ID and update them
      fragment.querySelectorAll('[id]').forEach(element => {
        const originalId = element.getAttribute('id');
        if (!originalId) return;
        
        const newId = `${originalId}_pickup`;
        element.setAttribute('id', newId);
        idMap.set(originalId, newId);
      });
      
      // Update all references in a single pass using the map
      idMap.forEach((newId, originalId) => {
        fragment.querySelectorAll(`label[for="${originalId}"], [form="${originalId}"]`).forEach(relatedEl => {
          if (relatedEl.tagName === 'LABEL') {
            relatedEl.setAttribute('for', newId);
          } else {
            relatedEl.setAttribute('form', newId);
            this.form = newId;
          }
        });
      });
    }

    setupAddToBagButton(fragment) {
      const atbEl = fragment.querySelector('buy-buttons button[type="submit"]');
      if (atbEl) {
        atbEl.addEventListener('click', () => {
          requestAnimationFrame(() => {
            const modal = document.querySelector('x-modal.modal--pickup[open]');
            if (modal) modal.hide();
          });
        });
      }
    }

    initializeStoreList() {
      requestAnimationFrame(() => {
        const variantPicker = this.containerEl.querySelector('variant-picker');
        const variantId = variantPicker?.selectedVariant?.id;
        
        if (variantId) {
          this.updateStoreList(variantId);
        }
        
        // Add event listener for variant changes
        const formElement = document.forms[this.form];
        if (formElement) {
          formElement.addEventListener("variant:change", this.handleVariantChange);
        }
      });
    }

    disconnectedCallback() {
      // No need to restore the image element since we cloned it
      
      // Remove pickup_modal from current URL
      const url = new URL(window.location.href);
      if (url.searchParams.has('pickup_modal')) {
        url.searchParams.delete('pickup_modal');
        history.replaceState({}, document.title, url);
      }

      // Clean up event listeners
      const formElement = document.forms[this.form];
      if (formElement) {
        formElement.removeEventListener("variant:change", this.handleVariantChange);
      }
    }
  }
  
  customElements.define('pickup-dynamic', PickupDynamic);
});
